import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, Smartphone, CreditCard, MessageSquare, BarChart3, 
  Building2, Bell, Shield, Users, Globe, ChevronRight,
  Check, Clock, TrendingUp, FileText, Zap, QrCode, Receipt
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const BRAND = { color: '#1e3a5f', accent: '#0d9488', light: '#f0fdfa' };

const features = [
  { icon: Smartphone, title: 'Portal de Clientes', desc: 'Tus clientes consultan y pagan desde el celular. Sin registros, sin fricciones.', color: '#0d9488' },
  { icon: CreditCard, title: 'Multi-Cobro Integrado', desc: 'Mercado Pago, transferencia, QR y mas. Cada empresa con sus credenciales.', color: '#2563eb' },
  { icon: MessageSquare, title: 'Campanas WhatsApp', desc: 'Recordatorios automaticos y masivos. Metricas de entrega en tiempo real.', color: '#16a34a' },
  { icon: BarChart3, title: 'Analytics en Tiempo Real', desc: 'Sabe quien consulto su deuda, cuando pago y cuanto recuperaste.', color: '#7c3aed' },
  { icon: FileText, title: 'Planes de Pago', desc: 'Crea planes de cuotas con interes configurable. El cliente elige como pagar.', color: '#dc2626' },
  { icon: Building2, title: 'Multi-Empresa', desc: 'Gestiona multiples empresas con branding, carteras y configuracion propia.', color: '#0891b2' },
];

const steps = [
  { num: 1, title: 'Configura tu empresa', desc: 'Personaliza tu portal con logo, colores y metodos de pago. Listo en minutos.' },
  { num: 2, title: 'Carga tus clientes', desc: 'Importa por Excel o carga manual. Asigna a carteras y campanas automaticamente.' },
  { num: 3, title: 'Cobra sin complicaciones', desc: 'Tus clientes reciben un link, consultan su cuenta y pagan. Vos monitoreas todo.' },
];

const benefits = [
  { icon: Clock, title: 'Ahorra tiempo', desc: 'Deja de perseguir pagos uno por uno. Automatiza todo.' },
  { icon: Shield, title: 'Seguro y confiable', desc: 'Tu plata va directo a tu cuenta. Sin intermediarios.' },
  { icon: Receipt, title: 'Todo ordenado', desc: 'Sabe quien pago, quien debe y cuanto te entra por mes.' },
  { icon: Globe, title: 'Desde cualquier lugar', desc: 'Gestiona tus cobranzas desde el celular o la computadora.' },
  { icon: Users, title: 'Para tu equipo', desc: 'Roles y permisos para que todo tu equipo trabaje coordinado.' },
  { icon: TrendingUp, title: 'Profesional', desc: 'Transmiti confianza a tus clientes con un sistema moderno.' },
];

// Animated counter
const AnimatedCounter = ({ end, prefix = '', suffix = '' }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const duration = 2000;
    const step = end / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [end]);
  return <span>{prefix}{count.toLocaleString('es-AR')}{suffix}</span>;
};

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Nav */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-md border-b border-slate-100 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2.5">
              <img src="/macropago-logo.jpeg" alt="MacroPago" className="w-9 h-9 rounded-lg object-cover" />
              <span className="text-xl font-bold tracking-tight" style={{ color: BRAND.color }}>MacroPago</span>
            </div>
            <div className="hidden md:flex items-center gap-6">
              <a href="#como-funciona" className="text-sm text-slate-600 hover:text-slate-900 font-medium transition-colors">Como funciona</a>
              <a href="#funcionalidades" className="text-sm text-slate-600 hover:text-slate-900 font-medium transition-colors">Funcionalidades</a>
              <a href="#beneficios" className="text-sm text-slate-600 hover:text-slate-900 font-medium transition-colors">Beneficios</a>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/login')} className="text-slate-600 hover:text-slate-900 font-medium hidden sm:inline-flex" data-testid="nav-login-button">
                Iniciar Sesion
              </Button>
              <Button onClick={() => navigate('/login')} className="text-white font-semibold h-10 px-5 rounded-full" style={{ backgroundColor: BRAND.accent }} data-testid="nav-cta-button">
                Empezar gratis
                <ArrowRight className="ml-1.5 w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-28 pb-16 md:pb-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <div className="inline-flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-full px-4 py-1.5 mb-6">
                <Zap className="w-3.5 h-3.5 text-teal-600" />
                <span className="text-xs text-teal-700 font-semibold">Para empresas, organismos publicos, PYMES y emprendedores</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-extrabold text-slate-900 mb-5 leading-[1.1] tracking-tight">
                La billetera virtual que{' '}
                <span style={{ color: BRAND.accent }}>simplifica los cobros</span>
              </h1>
              <p className="text-base md:text-lg text-slate-500 mb-8 leading-relaxed max-w-lg">
                Gestiona tus cobranzas de forma simple y profesional. Sin perseguir clientes, sin planillas, sin complicaciones.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button size="lg" onClick={() => navigate('/login')} className="h-12 px-8 text-base font-bold rounded-full text-white shadow-lg shadow-teal-200/50" style={{ backgroundColor: BRAND.accent }} data-testid="hero-get-started-button">
                  Empezar gratis
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => navigate('/t/demo')} className="h-12 px-8 text-base font-semibold rounded-full border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="hero-demo-button">
                  Ver demo en vivo
                </Button>
              </div>
              <div className="flex items-center gap-4 mt-8">
                <div className="flex -space-x-2">
                  {['#0d9488','#2563eb','#7c3aed','#dc2626'].map((c, i) => (
                    <div key={i} className="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-white text-[10px] font-bold shadow-sm" style={{ backgroundColor: c }}>
                      {['MP','GS','RC','CA'][i]}
                    </div>
                  ))}
                </div>
                <p className="text-sm text-slate-500">Empresas ya cobran sin complicaciones</p>
              </div>
            </motion.div>

            {/* Dashboard Preview */}
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="hidden lg:block">
              <div className="relative">
                <div className="bg-white rounded-2xl shadow-xl border border-slate-200/80 p-5 backdrop-blur-sm">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: BRAND.light }}>
                        <BarChart3 className="w-4.5 h-4.5" style={{ color: BRAND.accent }} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-900">Cobranzas del mes</p>
                        <p className="text-[11px] text-slate-400">Marzo 2026</p>
                      </div>
                    </div>
                    <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">+18% vs mes anterior</span>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 mb-4">
                    <p className="text-xs text-slate-400 mb-1">Total cobrado</p>
                    <p className="text-2xl font-bold text-slate-900">$<AnimatedCounter end={124500} /></p>
                    <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
                      <span>Progreso</span>
                      <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <motion.div initial={{ width: 0 }} animate={{ width: '70%' }} transition={{ duration: 1.5, delay: 0.5 }} className="h-full rounded-full" style={{ backgroundColor: BRAND.accent }} />
                      </div>
                      <span className="font-medium">16 de 23</span>
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-400 mb-2 font-medium uppercase tracking-wider">Ultimos movimientos</p>
                  <div className="space-y-2">
                    {[
                      { name: 'Maria Lopez', time: 'Hoy, 14:30', amount: '+$8.500', status: 'Pagado', color: 'emerald' },
                      { name: 'Carlos Ruiz', time: 'Ayer, 18:15', amount: '+$12.000', status: 'Pagado', color: 'emerald' },
                      { name: 'Ana Garcia', time: 'Vence manana', amount: '$6.000', status: 'Pendiente', color: 'amber' },
                    ].map(d => (
                      <div key={d.name} className="flex items-center justify-between py-2.5 px-3 bg-slate-50/80 rounded-lg hover:bg-slate-100 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${d.color === 'emerald' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}>
                            {d.name.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-800">{d.name}</p>
                            <p className="text-[11px] text-slate-400">{d.time}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-semibold ${d.color === 'emerald' ? 'text-emerald-600' : 'text-amber-600'}`}>{d.amount}</p>
                          <span className={`text-[10px] font-medium ${d.color === 'emerald' ? 'text-emerald-500' : 'text-amber-500'}`}>{d.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button className="w-full mt-3 text-xs text-teal-600 font-medium hover:underline">Ver todos los clientes...</button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trusted by / Social Proof */}
      <section className="py-10 px-4 sm:px-6 lg:px-8 bg-white border-y border-slate-100">
        <div className="max-w-5xl mx-auto">
          <p className="text-center text-xs font-semibold uppercase tracking-widest text-slate-400 mb-6">Ideal para</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Building2, label: 'Empresas' },
              { icon: Shield, label: 'Reparticiones publicas' },
              { icon: Users, label: 'PYMES' },
              { icon: Zap, label: 'Emprendedores' },
            ].map(item => (
              <div key={item.label} className="flex items-center justify-center gap-2.5 py-3 px-4 bg-slate-50 rounded-xl border border-slate-100">
                <item.icon className="w-4.5 h-4.5 text-teal-600 flex-shrink-0" />
                <span className="text-sm font-medium text-slate-700">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-white" id="como-funciona">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: BRAND.accent }}>Como funciona</p>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">Tres pasos simples para empezar a cobrar</h2>
            <p className="text-base text-slate-500 max-w-xl mx-auto">De forma ordenada y sin complicaciones</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <motion.div key={s.num} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="relative text-center md:text-left">
                <div className="w-12 h-12 rounded-2xl mx-auto md:mx-0 mb-5 flex items-center justify-center text-lg font-bold text-white shadow-lg" style={{ backgroundColor: BRAND.accent }}>
                  {s.num}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{s.desc}</p>
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute top-6 left-[calc(100%-16px)] w-12">
                    <ChevronRight className="w-5 h-5 text-slate-300" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Dashboard Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-slate-50" id="funcionalidades">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: BRAND.accent }}>Funcionalidades</p>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">Tu negocio, siempre bajo control</h2>
            <p className="text-base text-slate-500 max-w-2xl mx-auto">Visualiza quien pago, quien debe y cuanto vas recuperando, de un solo vistazo</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="bg-white border border-slate-200/80 rounded-xl p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all group" data-testid={`feature-card-${i}`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 transition-colors" style={{ backgroundColor: `${f.color}12` }}>
                  <f.icon className="w-5 h-5" style={{ color: f.color }} />
                </div>
                <h3 className="text-base font-bold text-slate-900 mb-1.5">{f.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-white" id="beneficios">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: BRAND.accent }}>Beneficios</p>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">Por que elegir MacroPago</h2>
            <p className="text-base text-slate-500 max-w-xl mx-auto">Disenado para quienes trabajan con clientes y necesitan cobrar de forma eficiente</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <motion.div key={b.title} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="flex gap-4 items-start">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${BRAND.accent}12` }}>
                  <b.icon className="w-5 h-5" style={{ color: BRAND.accent }} />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900 mb-1">{b.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{b.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: BRAND.color }}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Listo para simplificar tus cobros?</h2>
          <p className="text-base text-blue-200/80 mb-8 max-w-lg mx-auto">
            Unite a las empresas y emprendedores que ya cobran sin complicaciones con MacroPago.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" onClick={() => navigate('/login')} className="h-12 px-10 text-base font-bold rounded-full bg-white text-slate-900 hover:bg-slate-100 shadow-lg" data-testid="cta-button">
              Empezar gratis ahora <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate('/t/demo')} className="h-12 px-10 text-base font-semibold rounded-full border-2 border-white/30 text-white hover:bg-white/10 bg-transparent" data-testid="cta-demo-button">
              Ver demo
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12 px-4 sm:px-6 lg:px-8" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2.5 mb-4">
                <img src="/macropago-logo.jpeg" alt="MacroPago" className="w-9 h-9 rounded-lg object-cover" />
                <span className="text-lg font-bold text-white">MacroPago</span>
              </div>
              <p className="text-sm leading-relaxed max-w-sm">
                La billetera virtual que simplifica los cobros. Para empresas, reparticiones publicas, PYMES y emprendedores.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-3 uppercase tracking-wider">Producto</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#funcionalidades" className="hover:text-white transition-colors">Funcionalidades</a></li>
                <li><a href="#como-funciona" className="hover:text-white transition-colors">Como funciona</a></li>
                <li><a href="#beneficios" className="hover:text-white transition-colors">Beneficios</a></li>
                <li><button onClick={() => navigate('/t/demo')} className="hover:text-white transition-colors">Demo</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-3 uppercase tracking-wider">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors">Politica de Privacidad</button></li>
                <li><button onClick={() => navigate('/terms')} className="hover:text-white transition-colors">Terminos y Condiciones</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-6 text-center text-xs text-slate-500">
            <p>&copy; {new Date().getFullYear()} MacroPago. Todos los derechos reservados. macropago.com.ar</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
